import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  EmptyState,
  ErrorState,
  ListaSelecao,
  Modal,
  Nota,
  Pagination,
  SearchInput,
  Skeleton,
} from '../../../ui';

import { fetchVehicles, searchVehiclesByModel } from '../../../api/veiculos/veiculoApi';
import { BikeEletrica, listarBikesEletricas } from '../../../api/bike-eletrica/bikeEletricaApi';
import { numeroParaMoeda } from '../../../utils/moeda';
import { extrairMensagemErro } from '../../../utils/apiError';

/*
 * 🔴 ESTE IMPORT NÃO É USADO POR ESTE COMPONENTE — e não pode sair (at-59).
 *
 * `ModalVeiculos.css` é o **único lugar** que define cinco globais acidentais dos
 * quais ~12 telas legadas dependem (medido em 09/09/2026):
 *
 *   .form-button-search → 6 arquivos (carteira, /ti/envios, /ti/usuarios,
 *                         ModalRegionais, ModalCooperativas, …)
 *   .pagination         → 7      .span-text     → 6
 *   .search-input       → 4      .form-button-modal → 2
 *
 * ⚠️ Contagem reconferida na at-64: a tela de laudo por veículo (`LaudoHtml`)
 * saiu, mas o número de consumidores **não caiu a zero** em nenhuma das cinco —
 * o import continua sendo obrigatório.
 *
 * O CRA concatena todo CSS importado num bundle único, então basta UM importador
 * para as regras valerem em todas as telas — e este componente era o único.
 * Removendo o import, aquelas telas perdem estilo **sem erro, sem aviso**.
 *
 * ⚠️ **Por que o arquivo não foi movido nem podado:** `.form-button` é definida
 * aqui **e** em `ui/tokens/legado-compartilhado.css`, com valores diferentes (é o
 * achado C2 do `plan-c`). Mover as regras mudaria a ordem no bundle e poderia
 * inverter quem vence a cascata para os **33** consumidores de `.form-button`.
 * Mantendo o arquivo intacto e só o import, a cascata fica **provadamente igual**.
 *
 * 📌 As classes que ERAM exclusivas deste modal (`.search-container`,
 * `.close-button`, `.modal-list-veiculos`, `.veiculo-list`, `.modal-bike-ficha`,
 * `.modal-bike-sem-sga`, `.modal-fipe-referencia`) ficaram **sem consumidor** e
 * saem junto com o arquivo, na frente D.
 */
import './ModalVeiculos.css';

/**
 * Escolha do modelo do veículo (reescrito na at-59 sobre o `Modal` do kit).
 *
 * ## O que a reescrita mudou
 *
 *   1. **Visual do design system**, lista alcançável pelo teclado (era
 *      `<li onClick>`) e paginação em `<button>` (era `<div onClick>`).
 *   2. **A busca deixou de exigir clique num botão "Buscar"** — agora é o
 *      `SearchInput` do kit, com o debounce de 400 ms que é o padrão do projeto
 *      desde a at-10. Era o único campo de busca do app que precisava de clique.
 *   3. 🐛 **Fim da requisição em DOBRO ao paginar.** "Ant."/"Próx." chamavam
 *      `fetchVehicles` **à mão** e ainda mudavam `currentPage`, o que disparava o
 *      efeito que fazia a mesma requisição de novo — duas idas à FIPE por clique.
 *   4. 🐛 **A ordenação era inconsistente entre as páginas.** A página 1 vinha na
 *      ordem da API; a 2 em diante, e a busca, vinham ordenadas por nome. Trocar
 *      de página reordenava a lista sob o dedo do consultor. Agora ordena sempre.
 *   5. **Falha deixou de ser muda** — era toast + lista vazia, indistinguível de
 *      "esta marca não tem modelo neste ano". Agora `ErrorState` com "Tentar
 *      novamente", e o 404 continua sendo **lista vazia com explicação**, não erro.
 *
 * ## O que NÃO mudou, de propósito
 *
 * ⚠️ **As props são as mesmas** (`isOpen`/`onClose`/`onSelectVeiculo`/`year`/
 * `brand`/`bikeMarcaId`). 📌 **Atualização da at-62:** `views/cotacao` foi
 * migrada e não há mais consumidor legado — traduzi-las virou trabalho possível,
 * mas mecânico e transversal, da frente D.
 * 🔴 **A bike sai no MESMO SHAPE de um veículo FIPE** (`paraShapeDeVeiculo`), para
 * as telas tratarem os dois pela mesma callback (at-46).
 * 🔴 **O mês de referência da FIPE continua no topo, uma vez só** (at-21): ele vem
 * igual para todos os itens, e repetir por linha seria ruído. Ele existe porque o
 * espelho pode estar defasado — o valor sozinho engana.
 * 🔴 **"sem código no SGA" continua visível** por modelo: é o que decide se a
 * adesão vai poder subir ao SGA (at-46). Sem código, a adesão fecha no SAAC e o
 * envio fica pendente até o TI cadastrar o modelo fictício lá.
 */

interface Veiculo {
  vehicleType: number;
  price: string;
  brand: string;
  model: string;
  modelYear: number;
  fuel: string;
  codeFipe: string;
  referenceMonth: string;
  fuelAcronym: string;
}

/**
 * Bicicleta do catálogo devolvida NO MESMO SHAPE de um veículo FIPE (at-46), para
 * as telas de veículo tratarem os dois pela mesma callback. Os campos extras
 * (`bike`, `bikeEletricaId`, `codigoFipeSga`, `valorReferencia`) são o que a tela
 * usa para saber que é bike e preencher o Valor Protegido.
 */
export interface BikeSelecionada extends Veiculo {
  bike: true;
  bikeEletricaId: string;
  codigoFipeSga: string | null;
  valorReferencia: number;
}

export type VeiculoSelecionado = Veiculo | BikeSelecionada;

interface ModalVeiculosProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectVeiculo: (veiculo: VeiculoSelecionado) => void;
  year: number;
  brand: string;
  /**
   * Marca de motocicleta elétrica selecionada (at-46). Preenchido = o modal lista o
   * catálogo de bikes do SAAC em vez da FIPE (bicicleta não tem FIPE). O ano é
   * ignorado aqui — o catálogo pode não tê-lo.
   */
  bikeMarcaId?: string | null;
  /**
   * Veículo 0km (at-84): o modal lista o preço da tabela FIPE **0km** (ano 32000
   * da base) em vez do preço de usado do ano-modelo, e avisa isso no topo — pedido
   * de Victor, para o consultor saber que o valor considerado é o de veículo novo.
   * Só aparecem os modelos que também têm o ano `year` na FIPE: é o ano que vai
   * ao SGA, e ele o confere contra o código FIPE.
   */
  zeroKm?: boolean;
}

const paraShapeDeVeiculo = (b: BikeEletrica): BikeSelecionada => ({
  bike: true,
  bikeEletricaId: b.id,
  codigoFipeSga: b.codigoFipeSga,
  valorReferencia: b.valorReferencia,
  vehicleType: 0,
  price: numeroParaMoeda(b.valorReferencia),
  brand: b.marca,
  model: `${b.marca} ${b.modelo}`,
  modelYear: b.anoModelo ?? 0,
  fuel: 'Elétrico',
  fuelAcronym: 'E',
  // Bike não tem FIPE: o `codeFipe` recebe o código FICTÍCIO que o TI cadastrou no
  // SGA (pode ser vazio — aí a adesão fecha local e o envio fica pendente).
  codeFipe: b.codigoFipeSga ?? '',
  referenceMonth: '',
});

type Estado = 'carregando' | 'ok' | 'erro';

const POR_PAGINA = 10;

/** Ordem alfabética estável, insensível a acento — ver a correção 4 no topo. */
const porModelo = (a: Veiculo, b: Veiculo) =>
  a.model.localeCompare(b.model, 'pt', { sensitivity: 'base' });

const ModalVeiculos: React.FC<ModalVeiculosProps> = ({
  isOpen,
  onClose,
  onSelectVeiculo,
  year,
  brand,
  bikeMarcaId = null,
  zeroKm = false,
}) => {
  const modoBike = !!bikeMarcaId;

  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [total, setTotal] = useState(0);
  const [bikes, setBikes] = useState<BikeEletrica[]>([]);
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState('');
  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setBusca('');
    setPagina(1);
  }, [isOpen]);

  /*
   * Catálogo de bikes: lista pequena e **não paginada** pela API, filtrada no
   * cliente. Não depende de `pagina` de propósito — ver o comentário do modal de
   * marcas sobre por que os dois modos têm efeitos separados.
   */
  const buscarBikes = useCallback(async () => {
    setEstado('carregando');
    try {
      setBikes(await listarBikesEletricas({ marcaId: bikeMarcaId as string }));
      setEstado('ok');
    } catch (falha) {
      setErro(extrairMensagemErro(falha, 'Não foi possível carregar as motocicletas elétricas.'));
      setEstado('erro');
    }
  }, [bikeMarcaId]);

  const buscarNaFipe = useCallback(async () => {
    setEstado('carregando');
    try {
      const resposta = busca
        ? await searchVehiclesByModel(busca, year, brand, pagina, POR_PAGINA, zeroKm)
        : await fetchVehicles(year, brand, pagina, POR_PAGINA, zeroKm);

      setVeiculos([...(resposta.items || [])].sort(porModelo));
      setTotal(resposta.totalItems ?? 0);
      setEstado('ok');
    } catch (falha) {
      /*
       * 404 = marca sem modelo nesse ano (ex.: CALOI 2020, cuja FIPE só tem
       * mobiletes até 2002). É **lista vazia**, não erro — o `EmptyState` explica
       * o que fazer. Tratar como erro mandaria o consultor "tentar novamente"
       * numa consulta que vai falhar sempre.
       */
      const status = (falha as any)?.status ?? (falha as any)?.response?.status;
      if (status === 404) {
        setVeiculos([]);
        setTotal(0);
        setEstado('ok');
        return;
      }

      setErro(extrairMensagemErro(falha, 'Não foi possível carregar os modelos.'));
      setEstado('erro');
    }
  }, [busca, year, brand, pagina, zeroKm]);

  useEffect(() => {
    if (!isOpen || !modoBike) return;
    void buscarBikes();
  }, [isOpen, modoBike, buscarBikes]);

  useEffect(() => {
    if (!isOpen || modoBike) return;
    void buscarNaFipe();
  }, [isOpen, modoBike, buscarNaFipe]);

  /** Filtro do catálogo é local: a lista inteira já veio numa requisição. */
  const bikesVisiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return bikes;
    return bikes.filter((b) => `${b.marca} ${b.modelo}`.toLowerCase().includes(termo));
  }, [bikes, busca]);

  /*
   * Mês de referência da FIPE (at-21): vem igual para todos os itens, então é
   * exibido uma vez no topo em vez de repetido em cada linha.
   */
  const referenciaFipe = veiculos.find((v) => v.referenceMonth)?.referenceMonth ?? '';

  const escolher = (veiculo: VeiculoSelecionado) => {
    onSelectVeiculo(veiculo);
    onClose();
  };

  const fichaDaBike = (b: BikeEletrica) =>
    [
      b.potenciaW ? `${b.potenciaW} W` : null,
      b.bateriaWh ? `${b.bateriaWh} Wh` : null,
      b.autonomiaKm ? `${b.autonomiaKm} km` : null,
    ]
      .filter(Boolean)
      .join(' · ');

  return (
    <Modal
      aberto={isOpen}
      titulo={modoBike ? 'Selecionar Motocicleta Elétrica' : 'Selecionar Modelo'}
      descricao={
        modoBike
          ? 'Motocicleta elétrica não tem tabela FIPE: o valor é a referência do catálogo e pré-carrega o Valor Protegido, que você pode ajustar.'
          : referenciaFipe
            ? `Tabela FIPE${zeroKm ? ' 0km' : ''} — referência: ${referenciaFipe}`
            : undefined
      }
      tamanho="md"
      aoFechar={onClose}
      rodape={
        /* O catálogo de bikes não é paginado pela API — a lista vem inteira. */
        !modoBike && estado === 'ok' && veiculos.length > 0 ? (
          <Pagination
            pagina={pagina}
            totalItens={total}
            porPagina={POR_PAGINA}
            aoTrocar={setPagina}
          />
        ) : undefined
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <SearchInput
          aoBuscar={(termo) => {
            setBusca(termo);
            setPagina(1);
          }}
          placeholder="Buscar por modelo..."
          rotuloAcessivel="Buscar modelo"
        />

        {zeroKm && !modoBike && (
          <Nota>
            <strong>Preços da tabela FIPE 0km.</strong> É o valor de veículo novo, e não o
            do usado ano {year} — é ele que vai para o Valor FIPE e o Valor Protegido. Só
            aparecem os modelos que também têm o ano-modelo {year} na FIPE.
          </Nota>
        )}

        {estado === 'carregando' && <Skeleton linhas={5} altura="tw-h-11" />}

        {estado === 'erro' && (
          <ErrorState
            mensagem={erro}
            aoTentarNovamente={() => (modoBike ? void buscarBikes() : void buscarNaFipe())}
          />
        )}

        {estado === 'ok' &&
          (modoBike ? (
            <ListaSelecao
              itens={bikesVisiveis}
              chave={(b) => b.id}
              rotulo={(b) => b.modelo}
              detalhe={(b) => {
                const ficha = fichaDaBike(b);
                if (!b.codigoFipeSga) {
                  return (
                    <>
                      {ficha ? `${ficha} · ` : ''}
                      {/* Decide se a adesão vai poder subir ao SGA — ver o topo. */}
                      <span className="tw-font-semibold tw-text-amber-deep">
                        sem código no SGA
                      </span>
                    </>
                  );
                }
                return ficha || undefined;
              }}
              sufixo={(b) => numeroParaMoeda(b.valorReferencia)}
              aoEscolher={(b) => escolher(paraShapeDeVeiculo(b))}
              rotuloAcessivel="Motocicletas elétricas do catálogo"
              vazio={
                bikes.length === 0 ? (
                  <EmptyState
                    icone="sem-dados"
                    titulo="Esta marca ainda não tem modelos no catálogo"
                    descricao="O TI cadastra em Administração › Motocicletas Elétricas."
                  />
                ) : (
                  <EmptyState
                    icone="busca"
                    titulo="Nenhuma motocicleta elétrica encontrada para a busca"
                    descricao={`Nada corresponde a "${busca}". Tente escrever menos letras.`}
                  />
                )
              }
            />
          ) : (
            <ListaSelecao
              itens={veiculos}
              chave={(v) => v.codeFipe || v.model}
              rotulo={(v) => v.model}
              sufixo={(v) => v.price || undefined}
              aoEscolher={escolher}
              rotuloAcessivel="Modelos da tabela FIPE"
              vazio={
                <EmptyState
                  icone="sem-dados"
                  titulo={
                    zeroKm
                      ? `Nenhum modelo de ${brand} com preço FIPE 0km e ano-modelo ${year}`
                      : `Nenhum modelo na tabela FIPE para ${brand}${year ? ` em ${year}` : ''}`
                  }
                  descricao={
                    zeroKm
                      ? 'Confira o ano do modelo na nota fiscal. Se a FIPE ainda não tiver o modelo nesse ano, o cadastro não pode seguir: o SGA confere o ano contra a FIPE.'
                      : 'Confira o ano do modelo — ou, se for uma motocicleta elétrica, volte e escolha a marca pelo botão "Motocicleta elétrica".'
                  }
                />
              }
            />
          ))}
      </div>
    </Modal>
  );
};

export default ModalVeiculos;
